<template>
 <div id="app" class="modern-app" :class="{dark:darkMode}" data-native-contract="1" :data-native-ready="nativeReady ? 'true' : 'false'" :data-native-overlay="searchOpen || notifyOpen || navOpen ? 'true' : 'false'">
  <aside class="modern-sidebar"><div class="sidebar-caption">{{$t('appShell.workspace')}}</div><button v-for="(g,index) in groups" :key="g.label" :class="{selected:index===active}" @click="go(g.items[0][0])"><AppIcon :name="g.icon"/>{{g.label}}</button></aside>
  <van-pull-refresh v-model="refreshing" :disabled="!canPullRefresh" @refresh="onRefresh">
   <header class="modern-header" :class="{'is-hidden':headerHidden && !refreshing}">
    <div class="modern-navbar-viewport">
     <div class="modern-navbar-surface">
      <div class="modern-header-start">
       <button v-if="showBack" type="button" class="header-back" :aria-label="$t('appShell.back')" @click="back"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M19 12H5m6-6-6 6 6 6"/></svg></button>
       <h1 v-if="showBack" class="header-title">{{title}}</h1>
       <a v-else href="#/market" class="modern-brand" :aria-label="$t('appShell.home')">
        <BrandLockup/>
        <span class="brand-copy">
         <span class="brand-name"><strong>Token</strong><span>Trend</span></span>
         <small class="brand-tagline">{{$t('appShell.brandTagline')}}</small>
        </span>
       </a>
      </div>
      <div class="modern-header-actions">
       <button type="button" class="header-icon" :aria-label="$t(searchOpen?'appShell.closeSearch':'appShell.search')" @click="toggleSearch"><AppIcon :name="searchOpen?'close':'search'"/></button>
       <NotificationCenter ref="notifications" @open="onNotifyOpen"/>
       <a class="header-icon" href="#/my" :aria-label="$t('appShell.pages.my')">
        <AppIcon name="user"/>
       </a>
      </div>
     </div>
    </div>
   </header>
   <div v-if="searchOpen" class="modern-search"><NavHeader/></div>
   <main class="modern-main" :class="{'detail-main':showBack}">
    <div v-if="!showBack" class="modern-heading">
     <div class="page-heading-main">
      <div class="page-title-row">
       <h1>{{title}}</h1>
       <button v-if="showMenu" ref="menuTrigger" type="button" class="page-switch" :aria-expanded="navOpen" aria-haspopup="dialog" aria-controls="page-menu" @click="openMenu"><AppIcon name="grid"/><span>{{menuTitle}}</span><span class="page-switch-chevron" aria-hidden="true">›</span></button>
      </div>
     </div>
    </div>
    <router-view :key="pageKey" :dark-mode="darkMode" @update:darkMode="darkMode=$event"/>
   </main>
  </van-pull-refresh>
  <dialog ref="pageMenu" id="page-menu" class="page-menu-sheet" :class="{dragging:menuDragging}" :style="menuDragOffset ? {'--menu-drag-y':menuDragOffset+'px'} : null" aria-labelledby="page-menu-title" tabindex="-1" @close="onMenuClosed" @click="dismissBackdrop">
   <div class="page-menu-handle" aria-hidden="true" @pointerdown="startMenuDrag" @pointermove="moveMenuDrag" @pointerup="finishMenuDrag" @pointercancel="cancelMenuDrag"></div>
   <header class="page-menu-heading"><h2 id="page-menu-title">{{menuTitle}}</h2><button type="button" :aria-label="$t('appShell.closeMenu')" @click="closeMenu"><AppIcon name="close"/></button></header>
   <nav :aria-label="menuTitle" class="page-menu-items"><button v-for="item in menuItems" :key="item[0]" type="button" @click="go(item[0])"><span class="page-menu-icon"><AppIcon :name="itemIcon(item[0])"/></span><span class="page-menu-copy"><strong>{{item[1]}}</strong><small>{{$t('appShell.menuDescriptions.'+item[0])}}</small></span><span class="page-menu-chevron" aria-hidden="true">›</span></button></nav>
  </dialog>
  <LiquidTabBar :groups="groups" :active="tabActive" :dark-mode="darkMode" @select="go"/>
 </div>
</template>
<script>
import {groups,groupFor} from './app-navigation';
import {pageTitleKeys} from './page-titles';
import { applyUiLocale } from './i18n/ui-locale';
import AppIcon from './components/AppIcon.vue';
import BrandLockup from './components/BrandLockup.vue';
import NotificationCenter from './components/NotificationCenter.vue';
import LiquidTabBar from './components/LiquidTabBar.vue';
import { defineAsyncComponent } from 'vue';
const NavHeader = defineAsyncComponent(() => import('./layout/components/navHeader.vue'));
const darkModeKey='ui-dark';
function savedDarkMode(){try{return localStorage.getItem(darkModeKey)==='1'}catch(_){return false}}
function persistDarkMode(value){try{localStorage.setItem(darkModeKey,value?'1':'0')}catch(_){}}
export default {
 provide(){return {appTheme:{isDark:()=>this.darkMode,setDark:value=>{this.darkMode=!!value}}}},
 components:{AppIcon,BrandLockup,NotificationCenter,NavHeader,LiquidTabBar},
 data(){return {nativeReady:false,darkMode:savedDarkMode(),searchOpen:false,notifyOpen:false,navOpen:false,previousRoute:null,menuDragging:false,menuDragStart:0,menuDragOffset:0,headerHidden:false,refreshing:false,pageAttempt:0,pendingTabName:null,navigationAttempt:0}},
 computed:{
  groups(){return groups.map((g,index)=>({...g,label:this.$t(`appShell.groups.${index}`),items:g.items.map(([name])=>[name,this.$t(pageTitleKeys[name])])}))},
  active(){return groupFor(this.$route.name||'market')},
  tabActive(){return this.pendingTabName?groupFor(this.pendingTabName):this.active},
  group(){return this.groups[this.active]},
  title(){return this.$t(pageTitleKeys[this.$route.name]||'appShell.home')},
  showMenu(){return ['market','burst','publicChain','consult'].includes(this.$route.name)},
  menuTitle(){return this.$t(`appShell.menuLabels.${Math.min(this.active,3)}`)},
  menuItems(){return this.group.items.slice(1)},
  showBack(){return !['market','burst','publicChain','consult','my'].includes(this.$route.name)},
  pageKey(){return `${this.$route.fullPath}:${this.pageAttempt}`},
  canPullRefresh(){return !this.showBack && !this.searchOpen && !this.notifyOpen && !this.navOpen},
 },
 watch:{
  '$i18n.locale':{immediate:true,handler:applyUiLocale},
  darkMode:{immediate:true,handler(value){persistDarkMode(value);document.documentElement.classList.toggle('ui-dark',value);this.$nextTick(this.syncNativeTheme)}},
  $route(to,from){this.previousRoute=from.name;this.searchOpen=false;this.closeNotify();this.headerHidden=false;this.refreshing=false;this._lastScrollTarget=window;this._lastScrollY=window.scrollY;this._headerTravel=0;this.closeMenu()},
  title:{immediate:true,handler(value){document.title=`${value} · TokenTrend`}}
 },
 mounted(){this.$router.onReady(()=>this.$nextTick(()=>{this.nativeReady=true}));window.__tokenTrendBack=this.handleNativeBack;this.$nextTick(this.syncNativeTheme);this._lastScrollTarget=window;this._lastScrollY=window.scrollY;this._pendingScrollTarget=null;this._scrollFrame=null;window.addEventListener('scroll',this.handleScroll,{passive:true});document.addEventListener('scroll',this.handleScroll,{capture:true,passive:true})},
 beforeDestroy(){if(window.__tokenTrendBack===this.handleNativeBack)delete window.__tokenTrendBack;window.removeEventListener('scroll',this.handleScroll);document.removeEventListener('scroll',this.handleScroll,true);if(this._scrollFrame!==null)cancelAnimationFrame(this._scrollFrame)},
 methods:{
  handleNativeBack(){
   if(this.navOpen){this.closeMenu();return true}
   if(this.notifyOpen){this.closeNotify();return true}
   if(this.searchOpen){this.searchOpen=false;return true}
   return false;
  },
  syncNativeTheme(){
   const bridge=window.webkit?.messageHandlers?.appTheme;
   if(!bridge||!this.$el)return;
   const styles=getComputedStyle(this.$el);
   const read=name=>styles.getPropertyValue(name).trim();
   bridge.postMessage({background:read('--app-shell-background'),statusBar:read('--app-status-bar-background'),bottomBar:read('--app-bottom-bar-background')});
  },
  handleScroll(event){this._pendingScrollTarget=event?.target&&event.target!==document?event.target:window;if(this._scrollFrame!==null)return;this._scrollFrame=requestAnimationFrame(this.updateHeaderVisibility)},
  updateHeaderVisibility(){
   this._scrollFrame=null;
   const target=this._pendingScrollTarget||window;
   this._pendingScrollTarget=null;
   const y=Math.max(0,target===window?window.scrollY:target.scrollTop||0);
   if(target!==this._lastScrollTarget){this._lastScrollTarget=target;this._lastScrollY=y;this._headerTravel=0;return}
   const delta=y-this._lastScrollY;
   this._lastScrollY=y;
   if(window.innerWidth>=1000||y<=40||this.searchOpen||this.notifyOpen||this.navOpen||this.refreshing){this.headerHidden=false;this._headerTravel=0;return}
   if(!delta)return;
   const travel=this._headerTravel||0;
   this._headerTravel=(Math.sign(delta)===Math.sign(travel)?travel:0)+delta;
   // Require a deliberate direction change so scroll jitter does not interrupt the fade.
   if((!this.headerHidden&&this._headerTravel>=20)||(this.headerHidden&&this._headerTravel<=-12)){
    this.headerHidden=!this.headerHidden;
    this._headerTravel=0;
   }
  },
  onRefresh(){this.pageAttempt+=1;this.$nextTick(()=>{window.scrollTo(0,0);setTimeout(()=>{this.refreshing=false},500)})},
  toggleSearch(){this.closeNotify();this.searchOpen=!this.searchOpen},
  onNotifyOpen(open){this.notifyOpen=open;if(open)this.searchOpen=false},
  closeNotify(){this.$refs.notifications?.close();this.notifyOpen=false},
  openMenu(){this.closeNotify();const menu=this.$refs.pageMenu;if(typeof menu.showModal==='function')menu.showModal();else{menu.classList.add('dialog-fallback');menu.setAttribute('open','');}this.navOpen=true;this.$nextTick(()=>this.$refs.pageMenu.focus({preventScroll:true}))},
  closeMenu(){const menu=this.$refs.pageMenu;if(menu?.hasAttribute('open')){if(typeof menu.close==='function')menu.close();else menu.removeAttribute('open');}this.navOpen=false;this.resetMenuDrag()},
  startMenuDrag(event){if(event.button !== undefined && event.button !== 0)return;this.menuDragging=true;this.menuDragStart=event.clientY;this.menuDragOffset=0;try{event.currentTarget.setPointerCapture?.(event.pointerId)}catch{/* Synthetic pointers do not own capture. */}},
  moveMenuDrag(event){if(!this.menuDragging)return;this.menuDragOffset=Math.max(0,event.clientY-this.menuDragStart)},
  finishMenuDrag(){if(!this.menuDragging)return;const shouldClose=this.menuDragOffset>=Math.min(96,this.$refs.pageMenu.clientHeight*.25);if(shouldClose)this.closeMenu();else this.resetMenuDrag()},
  cancelMenuDrag(){this.resetMenuDrag()},
  onMenuClosed(){this.navOpen=false;this.resetMenuDrag()},
  resetMenuDrag(){this.menuDragging=false;this.menuDragStart=0;this.menuDragOffset=0},
  dismissBackdrop(event){if(event.target!==this.$refs.pageMenu)return;const r=event.target.getBoundingClientRect();if(event.clientX<r.left||event.clientX>r.right||event.clientY<r.top||event.clientY>r.bottom)this.closeMenu()},
  itemIcon(name){return ({collect:'star',platform:'globe',toolOutsidePrice:'swap',toolSentimentIndex:'pulse',calendar:'calendar',forum:'user',application:'grid',trace:'search',toolBlockChainBrowser:'search'})[name]||this.group.icon},
  async go(name){
   this.closeMenu();this.closeNotify();
   const attempt=++this.navigationAttempt;
   this.pendingTabName=name;
   try{await this.$router.push({name})}
   finally{if(attempt===this.navigationAttempt)this.pendingTabName=null}
  },
  back(){if(this.previousRoute)this.$router.back();else this.go(({solswap:'firmOffer',firmOffer:'publicChain',newCoinDetails:'newChain',insdetail:'inscription',forumDetails:'forum',consultDetails:'consult',hyperliquidDetails:'hyperliquid',burstCoinStatisticsDetails:'burst',burstHeatmapDetails:'burst'})[this.$route.name]||this.group.items[0][0])}
 }
};
</script>

<style>
/* Older iOS/WebView engines have no native dialog top layer. */
#app .page-menu-sheet:not([open]) { display: none; }
#app .page-menu-sheet.dialog-fallback[open] { display: block; z-index: 1000; }
</style>
